/**
 * Custom hook for feature flag actions (reset, update)
 */

import { useState } from 'react';
import { useAppDispatch } from '@/store';
import {
  resetFeatureFlag,
  updateOrganizationFlags,
  updatePropertyFlags,
} from '@/store/slices/admin/featureFlagsSlice';
import { FeatureFlagAttributes } from '@/types/admin/featureFlags';
import { showAlert } from '@/utils/common/alert';
import { FEATURE_FLAG_CONFIG, FeatureFlagKey } from '../constant';
import { getPropertyId } from '../utils/featureFlagsUtils';

interface UseFeatureFlagActionsProps {
  selectedFlag: FeatureFlagAttributes | null | undefined;
  isOrganizationView: boolean;
  localFlags: Record<string, any>;
  setLocalFlags: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}

export const useFeatureFlagActions = ({
  selectedFlag,
  isOrganizationView,
  localFlags,
  setLocalFlags,
}: UseFeatureFlagActionsProps) => {
  const dispatch = useAppDispatch();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  const handleResetFlag = async (key: string) => {
    if (!selectedFlag) return;

    try {
      if (isOrganizationView) {
        if (!selectedFlag.organization_id) {
          showAlert('Error', 'Organization ID not available');
          return;
        }

        const result = await dispatch(
          resetFeatureFlag({
            key,
            organizationId: selectedFlag.organization_id,
          })
        ).unwrap();

        if (result.updatedFlags?.flags) {
          setLocalFlags(result.updatedFlags.flags);
        }
      } else {
        const propertyId = getPropertyId(selectedFlag);
        if (!propertyId) {
          showAlert('Error', 'Property ID not available');
          return;
        }

        await dispatch(
          resetFeatureFlag({
            key,
            propertyId,
          })
        ).unwrap();

        const newFlags = { ...localFlags };
        delete newFlags[key];
        setLocalFlags(newFlags);
      }

      showAlert('Success', `Successfully reset ${key} to default value`);
    } catch (error: any) {
      showAlert('Error', `Failed to reset feature flag: ${error.message || 'Unknown error'}`);
    }
  };

  const handleUpdateFlag = async (key: string, newValue: any) => {
    if (!selectedFlag) return;

    try {
      if (isOrganizationView) {
        if (!selectedFlag.organization_id) {
          showAlert('Error', 'Organization ID not available');
          return;
        }
        await dispatch(
          updateOrganizationFlags({
            organizationId: selectedFlag.organization_id,
            flags: { [key]: newValue },
          })
        ).unwrap();
      } else {
        const propertyId = getPropertyId(selectedFlag);
        if (!propertyId) {
          showAlert('Error', 'Property ID not available');
          return;
        }
        await dispatch(
          updatePropertyFlags({
            propertyId,
            flags: { [key]: newValue },
          })
        ).unwrap();
      }
    } catch (error: any) {
      throw error;
    }
  };

  const handleSubmitValue = async (key: string) => {
    try {
      const config = FEATURE_FLAG_CONFIG[key as FeatureFlagKey];
      let normalizedValue: any = editValue;

      if (typeof config.defaultValue === 'boolean') {
        normalizedValue = editValue.toLowerCase() === 'true';
      } else if (typeof config.defaultValue === 'number') {
        normalizedValue = Number(editValue);
        if (Number.isNaN(normalizedValue)) {
          showAlert('Error', 'Invalid number value');
          return;
        }
      }

      await handleUpdateFlag(key, normalizedValue);

      setLocalFlags(prev => ({
        ...prev,
        [key]: normalizedValue,
      }));

      setEditingKey(null);
      setEditValue('');

      showAlert('Success', `Successfully updated ${key} to ${normalizedValue}`);
    } catch (error: any) {
      showAlert('Error', `Failed to update feature flag: ${error.message || 'Unknown error'}`);
    }
  };

  const startEditing = (key: string, currentValue: any) => {
    setEditingKey(key);
    setEditValue(currentValue.toString());
  };

  const cancelEditing = () => {
    setEditingKey(null);
    setEditValue('');
  };

  return {
    editingKey,
    editValue,
    setEditValue,
    handleResetFlag,
    handleUpdateFlag,
    handleSubmitValue,
    startEditing,
    cancelEditing,
  };
};

